/**
 * RecipientInput - Autocomplete input for selecting share recipients
 * Feature: 080-critique-sharing
 * Task: T054
 *
 * Allows users to search for registered users or enter email addresses
 * to share critique sessions with.
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { useSearchShareRecipientsGraphQL, ShareRecipient } from '@/hooks/graphql';
import { useRecentContactsGraphQL, EngagementContact } from '@/graphql/hooks';
import logger from '@/utils/logger';

/**
 * Convert EngagementContact to ShareRecipient format
 */
function contactToRecipient(contact: EngagementContact): ShareRecipient {
  if (contact.userId) {
    return {
      type: 'USER',
      userId: contact.userId,
      username: contact.username,
      displayName: contact.displayName,
      avatarUrl: contact.avatarUrl,
      email: null,
      isPending: false,
    };
  } else {
    return {
      type: 'EMAIL',
      userId: null,
      username: null,
      displayName: contact.email?.split('@')[0] || null,
      avatarUrl: null,
      email: contact.email,
      isPending: true,
    };
  }
}

// ============================================================================
// Types
// ============================================================================

export interface RecipientInputProps {
  /** Currently selected recipients */
  selectedRecipients: ShareRecipient[];
  /** Callback when recipients change */
  onRecipientsChange: (recipients: ShareRecipient[]) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Maximum number of recipients allowed */
  maxRecipients?: number;
}

// ============================================================================
// Component
// ============================================================================

export function RecipientInput({
  selectedRecipients,
  onRecipientsChange,
  placeholder = 'Search by username or enter email...',
  disabled = false,
  className,
  maxRecipients = 10,
}: RecipientInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchResults, setSearchResults] = useState<ShareRecipient[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { searchRecipients, loading } = useSearchShareRecipientsGraphQL();
  const { data: recentContacts, isLoading: loadingContacts } = useRecentContactsGraphQL(10);

  // Convert recent contacts to ShareRecipient format, filtering out already selected
  const filteredRecentContacts = useMemo(() => {
    const selectedIds = new Set(
      selectedRecipients
        .filter((r) => r.userId)
        .map((r) => r.userId)
    );
    const selectedEmails = new Set(
      selectedRecipients
        .filter((r) => r.email)
        .map((r) => r.email?.toLowerCase())
    );

    return recentContacts
      .filter((contact) => {
        if (contact.userId && selectedIds.has(contact.userId)) return false;
        if (contact.email && selectedEmails.has(contact.email.toLowerCase())) return false;
        return true;
      })
      .map(contactToRecipient);
  }, [recentContacts, selectedRecipients]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (inputValue.trim().length >= 2) {
        try {
          const results = await searchRecipients(inputValue);
          // Filter out already selected recipients
          const selectedIds = new Set(
            selectedRecipients
              .filter((r) => r.userId)
              .map((r) => r.userId)
          );
          const selectedEmails = new Set(
            selectedRecipients
              .filter((r) => r.email)
              .map((r) => r.email?.toLowerCase())
          );
          const filtered = results.filter((r) => {
            if (r.userId && selectedIds.has(r.userId)) return false;
            if (r.email && selectedEmails.has(r.email.toLowerCase())) return false;
            return true;
          });
          setSearchResults(filtered);
          setShowDropdown(true);
          setSelectedIndex(0);
        } catch (err) {
          logger.error('Sharing', 'Failed to search recipients', { error: err });
        }
      } else {
        setSearchResults([]);
        // Don't close dropdown here - we want to show recent contacts when focused
        // The click-outside handler will close it when user clicks away
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue, searchRecipients, selectedRecipients]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = useCallback(
    (recipient: ShareRecipient) => {
      if (selectedRecipients.length >= maxRecipients) {
        return;
      }
      onRecipientsChange([...selectedRecipients, recipient]);
      setInputValue('');
      setShowDropdown(false);
      setSearchResults([]);
      inputRef.current?.focus();
    },
    [selectedRecipients, onRecipientsChange, maxRecipients]
  );

  const handleRemove = useCallback(
    (index: number) => {
      const updated = [...selectedRecipients];
      updated.splice(index, 1);
      onRecipientsChange(updated);
    },
    [selectedRecipients, onRecipientsChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showDropdown || searchResults.length === 0) {
        // Handle backspace to remove last recipient
        if (e.key === 'Backspace' && inputValue === '' && selectedRecipients.length > 0) {
          handleRemove(selectedRecipients.length - 1);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % searchResults.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + searchResults.length) % searchResults.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (searchResults[selectedIndex]) {
            handleSelect(searchResults[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setShowDropdown(false);
          break;
        case 'Tab':
          if (searchResults[selectedIndex]) {
            e.preventDefault();
            handleSelect(searchResults[selectedIndex]);
          }
          break;
      }
    },
    [showDropdown, searchResults, selectedIndex, handleSelect, inputValue, selectedRecipients, handleRemove]
  );

  const atMax = selectedRecipients.length >= maxRecipients;

  return (
    <div className={cn('relative', className)}>
      {/* Input container with chips */}
      <div
        className={cn(
          'flex flex-wrap items-center gap-1.5 p-2 rounded-md border',
          'bg-background border-border',
          'focus-within:ring-2 focus-within:ring-ring',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {/* Selected recipient chips */}
        {selectedRecipients.map((recipient, index) => (
          <RecipientChip
            key={recipient.userId || recipient.email || index}
            recipient={recipient}
            onRemove={() => handleRemove(index)}
            disabled={disabled}
          />
        ))}

        {/* Input field */}
        {!atMax && (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              // Always show dropdown on focus - it will show loading, search results, or recent contacts
              setShowDropdown(true);
            }}
            placeholder={selectedRecipients.length === 0 ? placeholder : ''}
            disabled={disabled}
            className={cn(
              'flex-1 min-w-[120px] bg-transparent border-none outline-none',
              'text-sm text-foreground placeholder:text-muted-foreground'
            )}
            autoComplete="off"
            aria-label="Search for recipients"
          />
        )}
      </div>

      {/* Dropdown suggestions */}
      {showDropdown && !disabled && (
        <div
          ref={dropdownRef}
          className={cn(
            'absolute z-50 mt-1 w-full rounded-md border border-border',
            'bg-popover shadow-lg max-h-60 overflow-auto'
          )}
          role="listbox"
          aria-label="Recipient suggestions"
        >
          {/* Show loading state */}
          {(loading || loadingContacts) && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {inputValue.length >= 2 ? 'Searching...' : 'Loading contacts...'}
            </div>
          )}

          {/* Show search results when searching */}
          {!loading && inputValue.length >= 2 && (
            <>
              {searchResults.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  No users found
                </div>
              ) : (
                searchResults.map((recipient, index) => (
                  <RecipientOption
                    key={recipient.userId || recipient.email || index}
                    recipient={recipient}
                    isSelected={index === selectedIndex}
                    onClick={() => handleSelect(recipient)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  />
                ))
              )}
            </>
          )}

          {/* Show recent contacts when not searching */}
          {!loading && !loadingContacts && inputValue.length < 2 && (
            <>
              {filteredRecentContacts.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  No recent contacts. Start typing to search.
                </div>
              ) : (
                <>
                  <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider border-b border-border">
                    Recent
                  </div>
                  {filteredRecentContacts.map((recipient, index) => (
                    <RecipientOption
                      key={recipient.userId || recipient.email || index}
                      recipient={recipient}
                      isSelected={index === selectedIndex}
                      onClick={() => handleSelect(recipient)}
                      onMouseEnter={() => setSelectedIndex(index)}
                    />
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Helper text */}
      {atMax && (
        <p className="mt-1 text-xs text-muted-foreground">
          Maximum of {maxRecipients} recipients reached
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface RecipientChipProps {
  recipient: ShareRecipient;
  onRemove: () => void;
  disabled?: boolean;
}

function RecipientChip({ recipient, onRemove, disabled }: RecipientChipProps) {
  const displayName = recipient.type === 'USER'
    ? recipient.displayName || recipient.username || 'User'
    : recipient.email || 'Email';

  const isEmail = recipient.type === 'EMAIL';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm',
        isEmail
          ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
          : 'bg-primary/10 text-primary'
      )}
    >
      {isEmail && (
        <MailIcon className="h-3 w-3" />
      )}
      <span className="max-w-[150px] truncate">{displayName}</span>
      {!disabled && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-0.5 hover:text-red-500 focus:outline-none"
          aria-label={`Remove ${displayName}`}
        >
          <XIcon className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

interface RecipientOptionProps {
  recipient: ShareRecipient;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}

function RecipientOption({
  recipient,
  isSelected,
  onClick,
  onMouseEnter,
}: RecipientOptionProps) {
  const isEmail = recipient.type === 'EMAIL';

  return (
    <div
      role="option"
      aria-selected={isSelected}
      className={cn(
        'px-3 py-2 cursor-pointer flex items-center gap-2',
        isSelected ? 'bg-accent' : 'hover:bg-accent/50'
      )}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
    >
      {/* Avatar or icon */}
      <div
        className={cn(
          'h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium',
          isEmail
            ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
            : 'bg-primary/10 text-primary'
        )}
      >
        {isEmail ? (
          <MailIcon className="h-4 w-4" />
        ) : (
          (recipient.displayName?.[0] || recipient.username?.[0] || '?').toUpperCase()
        )}
      </div>

      {/* Name and details */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground truncate">
          {isEmail ? recipient.email : recipient.displayName || recipient.username}
        </div>
        {!isEmail && recipient.username && (
          <div className="text-xs text-muted-foreground truncate">
            @{recipient.username}
          </div>
        )}
        {isEmail && (
          <div className="text-xs text-muted-foreground">
            Send email invitation
          </div>
        )}
      </div>

      {/* Pending badge for email */}
      {isEmail && (
        <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-500/10 text-yellow-600 dark:text-yellow-400">
          Invite
        </span>
      )}
    </div>
  );
}

// ============================================================================
// Icons
// ============================================================================

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function MailIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}
